import {ResolvingLitElement} from "../../lib/ResolvingLitElement.js";
import {html} from "lit";
import {CustomerRepository} from "../../crm/CustomerRepository.js";
import {TimesheetEntryRepository} from "../TimesheetEntryRepository.js";
import {DateTime} from "luxon";
import {TimeTool} from "../../lib/TimeTool.js";
import {Chart} from "chart.js/auto";
import {HTML} from "../../lib/HTML.js";
import {mdiChevronRight} from "@mdi/js";
import {TimesheetReportTool} from "../TimesheetReportTool.js";

export class WeeklyTimeSummaryWidget extends ResolvingLitElement {

    static properties = {
        dayInWeek: { type: String, reflect: true },
        totalHours: { type: Number},
        billableHours: { type: Number},
        notBillableHours: { type: Number},
        plannedBillable: { type: Number},
        plannedNotBillable: { type: Number},
        formattedTotalHours: { type: String},
        formattedBillableHours: { type: String},
        formattedNotBillableHours: { type: String},
        formattedPlannedBillableHours: { type: String},
        formattedPlannedNotBillableHours: { type: String},
    }

    /**
     * @private {ReactiveArray<TimesheetEntry>}
     */
    _entries;

    /**
     * @protected {ReactiveArray<PlannedTime>}
     */
    _plannedTimes;

    /**
     * @private {Chart}
     */
    _chart;

    /**
     *
     * @protected {TimesheetReportTool}
     */
    _reportTool;

    constructor() {
        super();
        this.dayInWeek = TimeTool.dayAsString();
        this._billableHourColor = HTML.cssVariable('billable-color');
        this._unBillableHourColor = HTML.cssVariable('not-billable-color');
        this._plannedBillableHourColor = HTML.cssVariable('planned-billable-color');
        this._plannedUnBillableHourColor = HTML.cssVariable('planned-not-billable-color');
        this._reportTool = new TimesheetReportTool();
        this.billableHours = 0;
        this.notBillableHours = 0;
        this.plannedBillable = 40;
        this.plannedNotBillable = 4;




    }

    render() {
        return html`
            <div class="card weekly-time-summary">
                <header>
                    <h4>Week</h4>
                </header>
                <div class="row">
                    <div class="col-8">
                        <div id="week-chart"><canvas></canvas></div>
                    </div>
                    <div class="col-4 today-hours">
                        <div class="total-week-hours">${this.formattedTotalHours}<span class="of">/${this.formattedPlannedWeeklyTotal}</span></div>
                        <div class="progress-bar weekly-total-hour-progress" style="margin-top: -9px;"><div class="bar"></div></div>
                        <div class="billable-week-hours">${this.formattedBillableHours}<span class="of">/${this.formattedPlannedBillableHours}</span></div>
                        <div class="progress-bar weekly-total-billable-progress"><div class="bar"></div></div>
                        <div class="not-billable-week-hours">${this.formattedNotBillableHours}<span class="of">/${this.formattedPlannedNotBillableHours}</span></div>
                        <div class="progress-bar weekly-total-not-billable-progress"><div class="bar"></div></div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * @return {number}
     */
    get totalHours() {
        return this.billableHours + this.notBillableHours;
    }

    /**
     * @return {string}
     */
    get formattedTotalHours() {
        return TimeTool.formatDurationFromSeconds(this.totalHours, false);
    }

    /**
     * @return {number}
     */
    get plannedWeeklyTotal() {
        return this.plannedBillable + this.plannedNotBillable;
    }

    get formattedPlannedWeeklyTotal() {
        return TimeTool.formatDurationFromSeconds(this.plannedWeeklyTotal, false);
    }

    /**
     * @return {string}
     */
    get formattedBillableHours() {
        return TimeTool.formatDurationFromSeconds(this.billableHours, false);
    }

    get formattedNotBillableHours() {
        return TimeTool.formatDurationFromSeconds(this.notBillableHours, false);
    }

    /**
     * @return {string}
     */
    get formattedPlannedBillableHours() {
        return TimeTool.formatDurationFromSeconds(this.plannedBillable, false);
    }

    /**
     * @return {string}
     */
    get formattedPlannedNotBillableHours() {
        return TimeTool.formatDurationFromSeconds(this.plannedNotBillable, false);
    }

    createRenderRoot() {
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        this._entries = this._get('timesheet-entries');
        this._plannedTimes = this._get('planned-times');

    }

    disconnectedCallback() {
        super.disconnectedCallback();
        try {
            this._entries.filled.remove(this._updateWidgetData);
        } catch (e) {
            //
        }
        try {
            this._plannedTimes.filled.remove(this._updateWidgetData);
        } catch (e) {
            //
        }

    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this._chart = this._createChart();
        // They are always called after each other...
        this._entries.filled.listen(this._updateWidgetData);
        this._plannedTimes.filled.listen(this._updateWidgetData);
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (!_changedProperties.has('billableHours') && !_changedProperties.has('notBillableHours')) {
            return;
        }
        this._updateProgressBars();
    }

    _updateProgressBars() {
        let totalPercent = this._calcPercent(this.plannedWeeklyTotal, this.totalHours);
        this.renderRoot.querySelector('.weekly-total-hour-progress .bar').style.width = ''+Math.min(totalPercent, 100)+'%';
        let billablePercent = this._calcPercent(this.plannedBillable, this.billableHours);
        this.renderRoot.querySelector('.weekly-total-billable-progress .bar').style.width = ''+Math.min(billablePercent, 100)+'%';
        let notBillablePercent = this._calcPercent(this.plannedNotBillable, this.notBillableHours);
        this.renderRoot.querySelector('.weekly-total-not-billable-progress .bar').style.width = ''+Math.min(notBillablePercent, 100)+'%';
    }

    /**
     * @param {number} total
     * @param {number} done
     * @return {number}
     * @private
     */
    _calcPercent(total, done) {
        if (!done || done <= 0) {
            return 0;
        }
        return Math.round(done/(total/100));
    }

    _updateWidgetData = () => {

        let report = this._reportTool.createWeeklyReport(new Date(this.dayInWeek), this._entries.items(), this._plannedTimes.items());
        let billableSeries = [];
        let notBillableSeries = [];
        let plannedBillableSeries = [];
        let plannedNotBillableSeries = [];

        for (let day of report.durationMap.keys()) {
            billableSeries.push(report.durationMap.get(day).billable);
            notBillableSeries.push(report.durationMap.get(day).notBillable);
            plannedBillableSeries.push(report.durationMap.get(day).plannedBillable);
            plannedNotBillableSeries.push(report.durationMap.get(day).plannedNotBillable);
        }

        this._chart.data.datasets[0].data = billableSeries;
        this._chart.data.datasets[1].data = notBillableSeries;
        this._chart.data.datasets[2].data = plannedBillableSeries;
        this._chart.data.datasets[3].data = plannedNotBillableSeries;
        this.billableHours = report.billable;
        this.notBillableHours = report.notBillable;
        this.plannedBillable = report.plannedBillable;
        this.plannedNotBillable = report.plannedNotBillable;
        this._chart.update();

    }

    /**
     * @return {Chart}
     * @private
     */
    _createChart() {
        const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const data = {
            labels: labels,
            datasets: [
                {
                    label: 'Weekly billable hours',
                    data: [0, 0, 0, 0, 0, 0, 0],
                    backgroundColor: this._billableHourColor,
                    stack: 'logged'
                },
                {
                    label: 'Weekly non-billable hours',
                    data: [0, 0, 0, 0, 0, 0, 0],
                    backgroundColor: this._unBillableHourColor,
                    stack: 'logged'
                },
                {
                    label: 'Planned billable hours',
                    data: [0, 0, 0, 0, 0, 0, 0],
                    backgroundColor: this._plannedBillableHourColor,
                    stack: 'planned'
                },
                {
                    label: 'Planned non-billable hours',
                    data: [0, 0, 0, 0, 0, 0, 0],
                    backgroundColor: this._plannedUnBillableHourColor,
                    stack: 'planned'
                }
            ],

        };

        return new Chart(this.renderRoot.querySelector('#week-chart canvas'), {
            type: 'bar',
            data: data,
            options: {
                responsive: true,
                scales: {
                    x: {
                        stacked: true
                    },
                    y: {
                        beginAtZero: true,
                        stacked: true,
                        ticks: {
                            min: 0,
                            max: 43200,
                            stepSize: 3600,
                            suggestedMin: 28800,
                            suggestedMax: 43200,
                            callback: (label, index, labels) => {
                                let formatted = TimeTool.formatDurationFromSeconds(label, false);
                                return '' + parseInt(formatted.split(':')[0]) + 'h';
                            }
                        },
                    }
                },
                plugins: {
                    legend: {
                        display: false,

                    },
                    tooltip: {
                        callbacks: {
                            label: context => {
                                return TimeTool.formatDurationFromSeconds(context.raw);
                            }
                        }
                    }
                }
            },
        });

    }

}

customElements.define('x-weekly-time-summary-widget', WeeklyTimeSummaryWidget);